import { useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { fmtINR, fmtINRCompact } from "@/lib/format";
import { useAuth } from "@/contexts/AuthContext";
import { useNavigate } from "react-router-dom";
import VaultHealthCard from "@/components/VaultHealthCard";
import VaultBrief from "@/components/VaultBrief";
import MoneyMap from "@/components/MoneyMap";
import ForexTicker from "@/components/ForexTicker";
import { isDashboardV2Enabled, setDashboardV2 } from "@/lib/dashboardV2Flag";
import AskVaultHero from "@/components/AskVaultHero";
import OnboardingWizard from "@/components/OnboardingWizard";
import HomeHeroCard from "@/components/v2/HomeHeroCard";
import WhatsNewModal from "@/components/v2/WhatsNewModal";
import { useUiRedesignV2 } from "@/lib/uiRedesignV2";
import {
    Search, ShieldCheck, Zap, Upload, Banknote, Coins, Camera, FileText,
    Handshake, Repeat, FolderLock, CreditCard, Landmark, Globe, Receipt,
    Package, LineChart, TrendingUp, ClipboardCheck, Users as UsersIcon,
    Sparkles, ArrowRight, Wallet, Activity, AlertTriangle, Clock,
    ScrollText, PieChart, BadgeDollarSign,
} from "lucide-react";

/**
 * VAULT Concierge — single-page landing that replaces the heavy sidebar
 * first-experience. Guided, role-aware, tenant-scoped. Every KPI and
 * action lives inside the current tenant (`t(user)` on the backend).
 *
 * Sections rendered top→bottom:
 *   1. Welcome header + KPI strip
 *   2. What needs attention today (from dashboard summary)
 *   3. Quick action strip (role-gated)
 *   4. "I want to…" natural-language nav cards
 *   5. Grouped concierge sections (Money, Daily Finance, Docs & Assets, Security)
 */

function greetingFor(name) {
    const h = new Date().getHours();
    const part = h < 5 ? "Good evening" : h < 12 ? "Good morning" : h < 17 ? "Good afternoon" : "Good evening";
    return `${part}${name ? `, ${name.split(" ")[0]}` : ""}`;
}

function Badge({ tone = "neutral", children }) {
    const cls = {
        critical: "bg-rose-50 text-rose-700 border-rose-200",
        high:     "bg-amber-50 text-amber-800 border-amber-200",
        med:      "bg-blue-50 text-blue-700 border-blue-200",
        healthy:  "bg-emerald-50 text-emerald-700 border-emerald-200",
        neutral:  "bg-stone-100 text-stone-600 border-stone-200",
    }[tone] || "bg-stone-100 text-stone-600 border-stone-200";
    return <span className={`inline-flex items-center text-[10px] font-bold uppercase tracking-widest px-2 py-0.5 rounded border ${cls}`}>{children}</span>;
}

// ─── KPI strip ────────────────────────────────────────────────────
function Kpi({ icon: Icon, label, value, sub, to, tone = "neutral", testId }) {
    const toneRing = {
        healthy:  "ring-emerald-100 bg-emerald-50/40",
        warning:  "ring-amber-100 bg-amber-50/40",
        critical: "ring-rose-100 bg-rose-50/40",
        neutral:  "ring-stone-100",
    }[tone];
    return (
        <Link
            to={to}
            className={`group bg-white rounded-2xl ring-1 ${toneRing} p-4 hover:shadow-md hover:-translate-y-0.5 transition-all`}
            data-testid={testId}
        >
            <div className="flex items-center gap-2 mb-2">
                <Icon size={14} strokeWidth={1.8} className="text-stone-600" />
                <span className="text-[10px] font-bold uppercase tracking-widest text-stone-500">{label}</span>
            </div>
            <div className="text-2xl font-semibold text-stone-900 tabular-nums leading-none">{value}</div>
            {sub && <div className="text-[11px] text-stone-500 mt-1.5">{sub}</div>}
        </Link>
    );
}

// ─── Attention card ──────────────────────────────────────────────
function AttentionCard({ icon: Icon, tone, badge, title, hint, cta, to, testId }) {
    const bg = {
        critical: "border-rose-200 bg-rose-50/40",
        high:     "border-amber-200 bg-amber-50/50",
        med:      "border-blue-200 bg-blue-50/40",
        healthy:  "border-emerald-200 bg-emerald-50/40",
    }[tone] || "border-stone-200 bg-white";
    return (
        <Link
            to={to}
            className={`block rounded-2xl border ${bg} p-4 hover:shadow-md transition-all`}
            data-testid={testId}
        >
            <div className="flex items-start justify-between gap-2 mb-2">
                <div className="w-9 h-9 rounded-lg bg-white flex items-center justify-center border border-stone-200">
                    <Icon size={16} strokeWidth={1.8} className="text-stone-700" />
                </div>
                <Badge tone={tone}>{badge}</Badge>
            </div>
            <div className="text-[15px] font-semibold text-stone-900 leading-tight">{title}</div>
            <div className="text-[12px] text-stone-500 mt-1.5 min-h-[32px]">{hint}</div>
            <div className="mt-3 inline-flex items-center gap-1 text-[12px] font-semibold text-stone-900 group-hover:text-amber-700">
                {cta} <ArrowRight size={12} />
            </div>
        </Link>
    );
}

// ─── Quick-action pill ───────────────────────────────────────────
function QuickAction({ icon: Icon, label, to, testId }) {
    return (
        <Link
            to={to}
            data-testid={testId}
            className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full bg-white border border-stone-200 hover:border-amber-400 hover:bg-amber-50/40 transition-all text-sm font-medium text-stone-800"
        >
            <Icon size={13} strokeWidth={1.8} className="text-stone-600" />
            {label}
        </Link>
    );
}

// ─── Section header ──────────────────────────────────────────────
function SectionHeader({ title, subtitle }) {
    return (
        <div className="mb-3 mt-8">
            <h2 className="text-[11px] font-bold uppercase tracking-[0.2em] text-stone-500">{title}</h2>
            {subtitle && <p className="text-sm text-stone-600 mt-1">{subtitle}</p>}
        </div>
    );
}

// ─── Category tile (Money, Docs, etc.) ───────────────────────────
function CatTile({ icon: Icon, label, to, testId }) {
    return (
        <Link
            to={to}
            data-testid={testId}
            className="flex items-center gap-2.5 p-3 rounded-xl border border-stone-200 bg-white hover:border-amber-400 hover:bg-amber-50/40 transition-all group"
        >
            <span className="w-8 h-8 rounded-lg bg-stone-100 group-hover:bg-white flex items-center justify-center shrink-0">
                <Icon size={14} strokeWidth={1.8} className="text-stone-700" />
            </span>
            <span className="text-[13px] font-medium text-stone-800 flex-1 truncate">{label}</span>
            <ArrowRight size={12} className="text-stone-400 group-hover:text-amber-600 shrink-0" />
        </Link>
    );
}

export default function Concierge() {
    const { user } = useAuth();
    const navigate = useNavigate();
    const { enabled: v2Enabled } = useUiRedesignV2();
    const [ds, setDs] = useState(null);       // dashboard summary
    const [nw, setNw] = useState(null);       // net worth
    const [health, setHealth] = useState(null);
    const [cashPos, setCashPos] = useState(null);
    const [tenant, setTenant] = useState(null);
    const [showOnboarding, setShowOnboarding] = useState(false);

    useEffect(() => {
        if (!user) return;
        api.get("/dashboard/summary").then((r) => setDs(r.data)).catch(() => {});
        api.get("/net-worth").then((r) => setNw(r.data)).catch(() => {});
        api.get("/health-score").then((r) => setHealth(r.data)).catch(() => {});
        // NB: `/cash/summary` — NOT `/cash/position`. The latter never existed.
        api.get("/cash/summary").then((r) => setCashPos(r.data)).catch(() => {});
        api.get("/my-tenant").then((r) => setTenant(r.data?.tenant)).catch(() => {});
    }, [user?.id]);

    // Auto-forward founder-opted-in users to Command Centre V2. The flag is
    // strictly client-side (localStorage / `?v2=1`) so nothing changes for
    // customers who haven't opted in.
    useEffect(() => {
        if (isDashboardV2Enabled()) navigate("/dashboard-v2", { replace: true });
    }, [navigate]);

    // Auto-open OnboardingWizard once for a brand-new tenant: no cards +
    // no expenses AND user_preferences.onboarding.completed !== true.
    // Skipping still marks completed, so we don't nag on next login.
    useEffect(() => {
        if (!user || !ds) return;
        const isEmpty = Number(ds.active_cards || 0) === 0 && Number(ds.total_expenses_month || 0) === 0;
        const isDemoWs = tenant?.id === "global-demo" || user?.role === "demo";
        if (!isEmpty || isDemoWs) return;
        let cancelled = false;
        api.get("/user/preferences")
            .then((r) => {
                if (cancelled) return;
                const done = r.data?.onboarding?.completed === true;
                if (!done) setShowOnboarding(true);
            })
            .catch(() => { /* silent — never block dashboard on this */ });
        return () => { cancelled = true; };
    }, [user?.id, ds, tenant?.id]);

    const canAdd = ["admin", "finance", "expense_user"].includes(user?.role);
    const canFinance = ["admin", "finance"].includes(user?.role);
    const isAdmin = user?.role === "admin";
    const isDemo = user?.role === "demo" || user?.email?.includes("demo") || tenant?.id === "global-demo";

    // ─── Attention items (role-aware, driven by real backend metrics)
    // Field names match `/api/dashboard/summary` verbatim. Prior versions
    // referenced fields that don't exist (e.g. `payments_due_7d`,
    // `missing_receipts`) which quietly hid every attention card and
    // made the workspace *look* empty — the classic "data is there but
    // I can't see it" symptom. Fixed 2026-07-28.
    const attention = useMemo(() => {
        if (!ds) return [];
        const items = [];
        if (Number(ds.overdue_cards || 0) > 0) items.push({
            key: "overdue", icon: CreditCard, tone: "critical", badge: "Urgent",
            title: `${ds.overdue_cards} card${ds.overdue_cards === 1 ? "" : "s"} overdue`,
            hint: `${fmtINR(ds.overdue_amount || 0)} outstanding. Pay now to avoid interest and late fees.`,
            cta: "Pay now", to: "/cards?filter=overdue",
        });
        if (Number(ds.cards_due_next_7 || 0) > 0) items.push({
            key: "duesoon", icon: Banknote, tone: "high", badge: "Due soon",
            title: `${ds.cards_due_next_7} card${ds.cards_due_next_7 === 1 ? "" : "s"} due this week`,
            hint: "Record payments before the cycle closes to stay reconciled.",
            cta: "Record payment", to: "/payments?add=1",
        });
        if (Number(ds.missing_statements || 0) > 0) items.push({
            key: "missing", icon: FileText, tone: "high", badge: "Missing",
            title: `${ds.missing_statements} statement${ds.missing_statements === 1 ? "" : "s"} not uploaded yet`,
            hint: "Upload the PDF and VAULT will OCR it in seconds.",
            cta: "Upload statement", to: "/email-capture",
        });
        if (Number(ds.pending_payments || 0) > 0) items.push({
            key: "recon", icon: Repeat, tone: "med", badge: "Reconcile",
            title: `${ds.pending_payments} payment${ds.pending_payments === 1 ? "" : "s"} recorded, not confirmed yet`,
            hint: "We haven&apos;t seen these on a statement — reconcile when the bank confirms.",
            cta: "Open reconcile", to: "/reconciliation",
        });
        if (Number(ds.unreconciled_statements || 0) > 0) items.push({
            key: "unrec", icon: FileText, tone: "med", badge: "Review",
            title: `${ds.unreconciled_statements} statement${ds.unreconciled_statements === 1 ? "" : "s"} awaiting review`,
            hint: "Accept the OCR results so cash flow and closing are accurate.",
            cta: "Open review", to: "/statements/review",
        });
        if (Number(ds.high_utilization_count || 0) > 0) items.push({
            key: "util", icon: TrendingUp, tone: "med", badge: "This card needs review",
            title: `${ds.high_utilization_count} card${ds.high_utilization_count === 1 ? "" : "s"} over 70% utilised`,
            hint: "Splitting the balance or requesting a limit hike can protect your score.",
            cta: "Review cards", to: "/cards?filter=high-util",
        });
        if (cashPos && Number(cashPos.pending_verification || 0) > 0) items.push({
            key: "cash", icon: Coins, tone: "med", badge: "This week",
            title: `${cashPos.pending_verification} cash entr${cashPos.pending_verification === 1 ? "y" : "ies"} pending verification`,
            hint: "Physical cash across Petty · Service · Personal — do a quick tally.",
            cta: "Reconcile cash", to: "/cash-desk?tab=reconcile",
        });
        return items;
    }, [ds, cashPos]);

    // ─── Quick actions (role-gated) ──────────────────────────────
    const quickActions = [
        canAdd    && { icon: Zap,       label: "Add Expense",     to: "/quick-entry",            testId: "qa-expense" },
        canFinance && { icon: Camera,   label: "Upload Receipt",  to: "/smart-inbox?capture=1",  testId: "qa-receipt" },
        canFinance && { icon: Upload,   label: "Upload Statement",to: "/email-capture",          testId: "qa-statement" },
        canFinance && { icon: Banknote, label: "Record Payment",  to: "/payments?add=1",         testId: "qa-payment" },
        true      && { icon: Coins,     label: "Add Cash Entry",  to: "/cash-desk?add=sale",     testId: "qa-cash" },
        canFinance && { icon: Repeat,   label: "Reconcile Cash",  to: "/cash-desk?tab=reconcile",testId: "qa-recon" },
        canFinance && { icon: Repeat,   label: "Add Subscription",to: "/subscriptions?add=1",    testId: "qa-sub" },
        canFinance && { icon: FolderLock, label: "Upload Document", to: "/documents?add=1",     testId: "qa-doc" },
        canFinance && { icon: Handshake,label: "Generate CA Pack",to: "/auditor-shares?generate=1", testId: "qa-capack" },
    ].filter(Boolean);

    // ─── "I want to…" (natural-language nav) ─────────────────────
    const iWantTo = [
        { label: "Know what is due today",     to: "/finance-review",                testId: "iwt-due" },
        { label: "Upload a card statement",    to: "/email-capture",                 testId: "iwt-upload-stmt" },
        { label: "Record a payment",           to: "/payments?add=1",                testId: "iwt-pay" },
        { label: "Reconcile my cash",          to: "/cash-desk?tab=reconcile",       testId: "iwt-cash" },
        { label: "Check money leakage",        to: "/finance-review#leak",           testId: "iwt-leak" },
        { label: "Generate reports",           to: "/reports",                       testId: "iwt-reports" },
        { label: "Upload a document",          to: "/documents?add=1",               testId: "iwt-doc" },
        { label: "Review my net worth",        to: "/investments",                   testId: "iwt-nw" },
        isAdmin && { label: "Invite my finance team", to: "/users",                  testId: "iwt-invite" },
    ].filter(Boolean);

    const moneyCtrl = [
        { icon: CreditCard, label: "Credit Cards",   to: "/cards" },
        { icon: Landmark,   label: "Bank Accounts",  to: "/bank-accounts" },
        { icon: Coins,      label: "Cash Accounts",  to: "/cash-desk" },
        { icon: Globe,      label: "Forex Rates",    to: "/forex" },
        { icon: Banknote,   label: "Payments",       to: "/payments" },
        { icon: FileText,   label: "Statements",     to: "/statements" },
    ];
    const dailyFin = [
        { icon: Receipt,  label: "Expenses",         to: "/expenses" },
        { icon: Camera,   label: "Receipts",         to: "/smart-inbox" },
        { icon: Coins,    label: "Cash Ledger",      to: "/cash-desk?tab=ledger" },
        { icon: Wallet,   label: "Petty Cash",       to: "/cash-desk?account=petty" },
        { icon: Wallet,   label: "Service Cash",     to: "/cash-desk?account=service" },
        { icon: Wallet,   label: "Personal Cash",    to: "/cash-desk?account=personal" },
    ];
    const docsAssets = [
        { icon: Repeat,      label: "Subscriptions",     to: "/subscriptions" },
        { icon: Clock,       label: "Renewal Calendar",  to: "/calendar" },
        { icon: FolderLock,  label: "Document Vault",    to: "/documents" },
        { icon: ShieldCheck, label: "Warranties",        to: "/documents?type=warranty" },
        { icon: ShieldCheck, label: "Insurance",         to: "/documents?type=insurance" },
        { icon: LineChart,   label: "Investments",       to: "/investments" },
        { icon: Package,     label: "Assets",            to: "/assets" },
        { icon: PieChart,    label: "Net Worth",         to: "/finance-review#networth" },
    ];
    const security = [
        { icon: ShieldCheck, label: "Security Center",   to: "/security" },
        isAdmin && { icon: ScrollText, label: "Audit Logs", to: "/audit-trail" },
        isAdmin && { icon: UsersIcon,  label: "Users & Roles", to: "/users" },
        isAdmin && { icon: BadgeDollarSign, label: "Plan & Billing", to: "/settings/billing" },
    ].filter(Boolean);

    // ─── Render ──────────────────────────────────────────────────
    const workspaceName = tenant?.name || "Your workspace";
    const isNewUser = ds && (Number(ds.active_cards || 0) === 0)
                          && (Number(ds.total_expenses_month || 0) === 0);

    return (
        <div className="max-w-[1400px] mx-auto" data-testid="concierge-page">
            <OnboardingWizard
                forceOpen={showOnboarding}
                onClose={() => setShowOnboarding(false)}
            />
            {/* ═══ VAULT 2.0 · MIDNIGHT LUXE Home Hero (founder-gated, Phase 1) ═══ */}
            {v2Enabled && <HomeHeroCard />}
            <WhatsNewModal enabled={v2Enabled} />

            {/* ═══ Welcome header ═══ */}
            <div className="mb-8 flex items-start justify-between flex-wrap gap-4">
                <div>
                    <div className="eyebrow text-amber-700">VAULT Concierge</div>
                    <h1 className="font-display text-4xl sm:text-5xl text-stone-900 leading-none mt-2" data-testid="concierge-greeting">
                        {isDemo ? "Welcome to VAULT Demo" : `${greetingFor(user?.name)}.`}
                    </h1>
                    <p className="text-sm text-stone-500 mt-3 max-w-2xl">
                        {isDemo
                            ? "Explore how VAULT helps manage money, assets, documents, and financial actions. All numbers below are demo data."
                            : "Your financial life. Organised. Here's what matters in your VAULT today."}
                    </p>
                    <div className="flex items-center gap-2 mt-4 text-[11px] text-stone-500 flex-wrap">
                        <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-stone-100 border border-stone-200">
                            <Wallet size={11} /> {workspaceName}
                        </span>
                        <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-stone-100 border border-stone-200 uppercase tracking-widest">
                            {user?.role || "member"}
                        </span>
                        <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-emerald-50 border border-emerald-200 text-emerald-700">
                            <ShieldCheck size={11} /> Secure workspace
                        </span>
                        {!isDemo && tenant?.id !== "global-demo" && (
                            <button
                                onClick={async () => {
                                    try {
                                        await api.post("/workspace/switch");
                                        window.location.reload();
                                    } catch { /* silent */ }
                                }}
                                data-testid="concierge-try-demo-btn"
                                className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-amber-50 border border-amber-200 text-amber-800 hover:bg-amber-100 transition-colors"
                            >
                                <Sparkles size={11} /> Explore the VAULT Demo
                            </button>
                        )}
                        {isAdmin && !isDemo && (
                            <button
                                onClick={() => { setDashboardV2(true); navigate("/dashboard-v2"); }}
                                data-testid="try-command-centre-v2"
                                className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-stone-900 border border-stone-900 text-white hover:bg-stone-800 transition-colors"
                                title="Founder preview — Dashboard V2"
                            >
                                <Sparkles size={11} /> Try Command Centre v2
                            </button>
                        )}
                        {ds?.last_activity_at && (
                            <span className="text-stone-400">· updated {new Date(ds.last_activity_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}</span>
                        )}
                    </div>
                </div>

                {/* Search / command bar */}
                <button
                    onClick={() => {
                        const ev = new KeyboardEvent("keydown", { key: "k", metaKey: true, bubbles: true });
                        document.dispatchEvent(ev);
                    }}
                    className="w-full sm:w-96 h-11 inline-flex items-center gap-2 px-4 rounded-full bg-white border border-stone-200 hover:border-stone-400 text-sm text-stone-500 text-left transition-colors"
                    data-testid="concierge-search"
                >
                    <Search size={13} className="text-stone-400 shrink-0" />
                    <span className="truncate">Search or type an action… <span className="text-stone-400">e.g. &ldquo;upload statement&rdquo;</span></span>
                    <span className="ml-auto text-[10px] text-stone-400 border border-stone-200 rounded px-1.5 py-0.5">⌘K</span>
                </button>
            </div>

            {/* ═══ KPI strip ═══ */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-8" data-testid="concierge-kpis">
                <Kpi
                    icon={Activity}
                    label="Finance health"
                    value={health?.score != null ? `${health.score}/100` : "—"}
                    sub={health?.status ? `Status: ${health.status}` : "Live score"}
                    tone={(health?.score >= 80) ? "healthy" : (health?.score >= 60) ? "warning" : "critical"}
                    to="/health-score"
                    testId="kpi-health"
                />
                <Kpi
                    icon={PieChart}
                    label="Net worth"
                    value={nw ? fmtINRCompact(nw.net_worth || 0) : "—"}
                    sub={nw ? `${fmtINRCompact(nw.total_assets || 0)} assets − ${fmtINRCompact(nw.total_liabilities || 0)} liab` : ""}
                    tone="neutral"
                    to="/investments"
                    testId="kpi-networth"
                />
                <Kpi
                    icon={CreditCard}
                    label="Total outstanding"
                    value={ds ? fmtINRCompact(ds.total_outstanding || 0) : "—"}
                    sub={ds ? `${ds.active_cards || 0} active cards` : ""}
                    tone={Number(ds?.total_outstanding || 0) > 100000 ? "warning" : "neutral"}
                    to="/cards"
                    testId="kpi-outstanding"
                />
                <Kpi
                    icon={Coins}
                    label="Cash position"
                    value={cashPos ? fmtINR(cashPos.total || 0) : "—"}
                    sub={cashPos ? `${cashPos.accounts?.length || 0} cash accounts` : ""}
                    tone="healthy"
                    to="/cash-desk"
                    testId="kpi-cash"
                />
            </div>

            {/* ═══ What needs attention today ═══ */}
            <SectionHeader
                title="What needs attention today"
                subtitle={attention.length === 0 ? "Everything's healthy. Nicely done." : `${attention.length} item${attention.length === 1 ? "" : "s"} to review.`}
            />
            {attention.length === 0 ? (
                <div className="rounded-2xl border border-emerald-200 bg-emerald-50/40 py-8 px-6 text-center" data-testid="concierge-attention-empty">
                    <ShieldCheck size={24} className="text-emerald-600 mx-auto mb-2" />
                    <div className="text-[15px] font-semibold text-stone-900">You&apos;re all caught up.</div>
                    <div className="text-[12px] text-stone-500 mt-1">No overdue payments, missing statements, or leaks detected right now.</div>
                </div>
            ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3" data-testid="concierge-attention">
                    {attention.map((a) => {
                        // Spread all fields EXCEPT key/testId directly so React 19 doesn't warn.
                        const { key: _k, ...rest } = a;
                        return <AttentionCard key={a.key} {...rest} testId={`attention-${a.key}`} />;
                    })}
                </div>
            )}

            {/* ═══ Quick actions ═══ */}
            <SectionHeader title="Quick actions" subtitle="One-tap access to the things you do every day." />
            <div className="flex flex-wrap gap-2" data-testid="concierge-quick-actions">
                {quickActions.map((qa) => <QuickAction key={qa.label} {...qa} />)}
            </div>

            {/* ═══ First-time setup checklist ═══ */}
            {isNewUser && !isDemo && (
                <div className="mt-8 rounded-2xl border border-amber-200 bg-gradient-to-br from-amber-50 to-white p-5" data-testid="concierge-setup">
                    <div className="flex items-center gap-2 mb-2">
                        <Sparkles size={14} className="text-amber-700" />
                        <div className="text-[11px] font-bold uppercase tracking-widest text-amber-800">Set up your VAULT in 5 steps</div>
                    </div>
                    <h3 className="text-xl font-semibold text-stone-900">Let&apos;s get you productive in 3 minutes.</h3>
                    <div className="mt-4 grid grid-cols-1 md:grid-cols-5 gap-2 text-[13px]">
                        {[
                            { label: "Choose usage type", to: "/setup" },
                            { label: "Add first card", to: "/cards/new" },
                            { label: "Add cash account", to: "/cash-desk" },
                            { label: "Upload statement", to: "/email-capture" },
                            { label: "Invite team", to: "/users" },
                        ].map((s, i) => (
                            <Link
                                key={i}
                                to={s.to}
                                data-testid={`setup-step-${i}`}
                                className="rounded-xl border border-amber-200 bg-white/80 px-3 py-2.5 flex items-center gap-2 hover:border-amber-400 transition-colors"
                            >
                                <span className="w-5 h-5 rounded-full bg-amber-100 text-amber-800 text-[11px] font-bold flex items-center justify-center shrink-0">{i + 1}</span>
                                <span className="text-stone-800 truncate">{s.label}</span>
                            </Link>
                        ))}
                    </div>
                    <div className="text-[11px] text-stone-500 mt-3">Setup progress: 20% complete · You can skip any step and return later.</div>
                </div>
            )}

            {/* ═══ VAULT Brief · Money Map · Health · Ask VAULT — signature row ═══ */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-3 mb-4">
                <div className="lg:col-span-2"><VaultBrief firstName={user?.name?.split(" ")[0] || "there"} /></div>
                <VaultHealthCard />
            </div>
            <div className="mb-4"><AskVaultHero /></div>
            <div className="mb-8"><MoneyMap /></div>

            {/* ═══ Currency Watch · ambient market awareness ═══ */}
            <div className="mb-8" data-testid="concierge-currency-watch">
                <div className="flex items-baseline justify-between mb-3">
                    <div>
                        <div className="eyebrow text-amber-700">Currency Watch</div>
                        <div className="text-sm text-stone-500 mt-1">Currencies you care about, at a glance. Rates are indicative — final billing uses statement values.</div>
                    </div>
                    <Link
                        to="/forex"
                        data-testid="concierge-currency-watch-open"
                        className="text-[12px] font-semibold text-stone-700 hover:text-amber-700 inline-flex items-center gap-1"
                    >
                        Open Live Forex <ArrowRight size={12} />
                    </Link>
                </div>
                <ForexTicker />
            </div>

            {/* ═══ I want to… ═══ */}
            <SectionHeader title="What would you like to do?" subtitle="Say it plainly — we&rsquo;ll take you there." />
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2" data-testid="concierge-iwant">
                {iWantTo.map((i) => (
                    <button
                        key={i.testId}
                        onClick={() => navigate(i.to)}
                        data-testid={i.testId}
                        className="text-left p-3 rounded-xl border border-stone-200 bg-white hover:border-amber-400 hover:bg-amber-50/40 transition-all flex items-center gap-2"
                    >
                        <span className="text-amber-700 shrink-0">›</span>
                        <span className="text-[13.5px] text-stone-800 flex-1">I want to {i.label.toLowerCase()}</span>
                        <ArrowRight size={12} className="text-stone-400" />
                    </button>
                ))}
            </div>

            {/* ═══ Money Control ═══ */}
            <SectionHeader title="Money control" subtitle="Manage cards, banks, cash, forex, dues, and payment tracking." />
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-2">
                {moneyCtrl.map((c) => (
                    <CatTile key={c.label} {...c} testId={`cat-money-${c.label.toLowerCase().replace(/\s+/g, "-")}`} />
                ))}
            </div>

            {/* ═══ Daily Finance ═══ */}
            <SectionHeader title="Daily finance" subtitle="Track daily spends, cash movements, receipts, and finance activity." />
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-2">
                {dailyFin.map((c) => (
                    <CatTile key={c.label} {...c} testId={`cat-daily-${c.label.toLowerCase().replace(/\s+/g, "-")}`} />
                ))}
            </div>

            {/* ═══ Documents & Assets ═══ */}
            <SectionHeader title="Documents &amp; assets" subtitle="Keep recurring payments, renewals, documents, and proofs organized." />
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2">
                {docsAssets.map((c) => (
                    <CatTile key={c.label} {...c} testId={`cat-docs-${c.label.toLowerCase().replace(/\s+/g, "-")}`} />
                ))}
            </div>

            {/* ═══ Security & Trust ═══ */}
            <SectionHeader title="Security &amp; trust" subtitle="Control access, safety, permissions, logs, and workspace rules." />
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-10">
                {security.map((c) => (
                    <CatTile key={c.label} {...c} testId={`cat-security-${c.label.toLowerCase().replace(/\s+/g, "-")}`} />
                ))}
                <CatTile icon={ClipboardCheck} label="All tools" to="/tools" testId="cat-security-all-tools" />
            </div>

            {/* ═══ Demo conversion CTA ═══ */}
            {isDemo && (
                <div className="mt-10 rounded-2xl border border-amber-300 bg-gradient-to-br from-amber-50 via-white to-emerald-50 p-6 flex items-start gap-4" data-testid="concierge-demo-cta">
                    <div className="w-12 h-12 rounded-full bg-amber-100 flex items-center justify-center shrink-0">
                        <Sparkles size={18} className="text-amber-700" />
                    </div>
                    <div className="flex-1">
                        <h3 className="text-lg font-semibold text-stone-900">Like what you see?</h3>
                        <p className="text-sm text-stone-600 mt-1">Create your own secure workspace and start with your own data. Invite your finance team when you&apos;re ready.</p>
                        <div className="mt-3 flex flex-wrap gap-2">
                            <Link to="/signup" className="px-4 py-2 rounded-full bg-stone-900 text-white text-sm font-semibold" data-testid="demo-cta-signup">Create Workspace</Link>
                            <Link to="/contact?topic=sales" className="px-4 py-2 rounded-full border border-stone-300 text-sm font-semibold" data-testid="demo-cta-contact">Request Demo</Link>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
